import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { resendService } from '../services/resendService';
import { SharedLayout } from '../components/layout/SharedLayout';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px;
  margin-top: -80px;
  padding-top: 140px;
  text-align: center;
  color: white;
`;

const HeroTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 24px);
  margin-bottom: 40px;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
`;

const MainContent = styled.main`
  background: linear-gradient(135deg, #fff9f0 0%, #ffe8d6 100%);
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: ${theme.spacing['2xl']} ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(28px, 4vw, 40px);
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
`;

const CTADescription = styled.p`
  font-size: clamp(18px, 2vw, 20px);
  color: #666;
  margin-bottom: 40px;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 18px 40px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  }
`;

const CTAPhone = styled.a`
  display: block;
  margin-top: 20px;
  font-size: 24px;
  font-weight: bold;
  color: #232B3B;
  text-decoration: none;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
  }
`;

const CalculatorCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${theme.spacing['2xl']};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xl};
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: ${theme.spacing.xl};
  font-size: 2.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: ${theme.spacing['2xl']};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: ${theme.spacing.xl};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: linear-gradient(90deg, #ffd700 0%, #ffed4e 100%);
  transition: width 0.3s ease;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormSection = styled.div<{ isActive: boolean }>`
  display: ${props => props.isActive ? 'flex' : 'none'};
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  background: white;

  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: space-between;
  margin-top: ${theme.spacing.lg};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  background: ${props => props.variant === 'secondary' ? 'white' : 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'};
  color: ${props => props.variant === 'secondary' ? '#333' : '#333'};
  border: ${props => props.variant === 'secondary' ? '2px solid #ffd700' : 'none'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultsSection = styled.div`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 237, 78, 0.1) 100%);
  padding: ${theme.spacing.xl};
  border-radius: 12px;
  margin-top: ${theme.spacing.xl};
`;

const ResultsTitle = styled.h2`
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ResultCard = styled.div`
  background: white;
  padding: ${theme.spacing.md};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ResultLabel = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xs};
`;

const ResultValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #ffd700;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.9rem;
  margin-top: ${theme.spacing.xs};
`;

const SuccessMessage = styled.div`
  background: #10b981;
  color: white;
  padding: ${theme.spacing.md};
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: ${theme.spacing.lg};
`;

interface FormData {
  // Step 1: Personal Information
  fullName: string;
  email: string;
  phone: string;
  currentAge: string;
  retirementAge: string;

  // Step 2: Financial Information
  currentSavings: string;
  monthlyContribution: string;
  expectedReturn: string;
  inflationRate: string;

  // Step 3: Retirement Goals
  monthlyRetirementIncome: string;
  retirementLifestyle: string;
  healthcareCosts: string;
  additionalGoals: string;
}

interface CalculationResults {
  retirementNeeded: number;
  projectedSavings: number;
  savingsGap: number;
  recommendedMonthly: number;
  yearsToRetirement: number;
  inflationAdjustedGoal: number;
}

export const RetirementCalculator: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    currentAge: '',
    retirementAge: '',
    currentSavings: '',
    monthlyContribution: '',
    expectedReturn: '7',
    inflationRate: '3',
    monthlyRetirementIncome: '',
    retirementLifestyle: 'moderate',
    healthcareCosts: '',
    additionalGoals: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = () => {
    const newErrors: Partial<FormData> = {};

    if (currentStep === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.currentAge) newErrors.currentAge = 'Current age is required';
      if (!formData.retirementAge) newErrors.retirementAge = 'Retirement age is required';
      if (Number(formData.retirementAge) <= Number(formData.currentAge)) {
        newErrors.retirementAge = 'Retirement age must be greater than current age';
      }
    }

    if (currentStep === 2) {
      if (!formData.currentSavings) newErrors.currentSavings = 'Current savings is required';
      if (!formData.monthlyContribution) newErrors.monthlyContribution = 'Monthly contribution is required';
    }

    if (currentStep === 3) {
      if (!formData.monthlyRetirementIncome) newErrors.monthlyRetirementIncome = 'Monthly income goal is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        calculateResults();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const calculateResults = () => {
    const currentAge = Number(formData.currentAge);
    const retirementAge = Number(formData.retirementAge);
    const currentSavings = Number(formData.currentSavings);
    const monthlyContribution = Number(formData.monthlyContribution);
    const expectedReturn = Number(formData.expectedReturn) / 100;
    const inflationRate = Number(formData.inflationRate) / 100;
    const monthlyRetirementIncome = Number(formData.monthlyRetirementIncome);
    const healthcareCosts = Number(formData.healthcareCosts) || 500;

    const yearsToRetirement = retirementAge - currentAge;
    const monthsToRetirement = yearsToRetirement * 12;
    const yearsInRetirement = 30; // Assume 30 years in retirement

    // Calculate future value of current savings
    const futureValueCurrentSavings = currentSavings * Math.pow(1 + expectedReturn, yearsToRetirement);

    // Calculate future value of monthly contributions
    const monthlyReturn = expectedReturn / 12;
    const futureValueContributions = monthlyContribution * 
      ((Math.pow(1 + monthlyReturn, monthsToRetirement) - 1) / monthlyReturn);

    const projectedSavings = futureValueCurrentSavings + futureValueContributions;

    // Calculate inflation-adjusted retirement goal
    const totalMonthlyNeeded = monthlyRetirementIncome + healthcareCosts;
    const inflationAdjustedMonthly = totalMonthlyNeeded * Math.pow(1 + inflationRate, yearsToRetirement);
    const inflationAdjustedGoal = inflationAdjustedMonthly * 12 * yearsInRetirement;

    // Calculate savings gap
    const savingsGap = Math.max(0, inflationAdjustedGoal - projectedSavings);

    // Calculate recommended monthly contribution to close the gap
    let recommendedMonthly = monthlyContribution;
    if (savingsGap > 0) {
      recommendedMonthly = (savingsGap / ((Math.pow(1 + monthlyReturn, monthsToRetirement) - 1) / monthlyReturn)) + monthlyContribution;
    }

    const calculatedResults: CalculationResults = {
      retirementNeeded: inflationAdjustedGoal,
      projectedSavings,
      savingsGap,
      recommendedMonthly,
      yearsToRetirement,
      inflationAdjustedGoal: inflationAdjustedMonthly
    };

    setResults(calculatedResults);
    sendResults(calculatedResults);
  };

  const sendResults = async (calculatedResults: CalculationResults) => {
    setIsSubmitting(true);
    
    // Format data for resendService which expects specific fields
    const formDataForEmail = {
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      // Add dummy fields that resendService expects but we'll override in message
      insuranceCompany: 'N/A - Retirement Calculator',
      annuityProduct: 'Retirement Planning',
      yearPurchased: new Date().getFullYear().toString(),
      currentAge: formData.currentAge,
      initialInvestment: formData.currentSavings,
      primaryGoal: 'Retirement Planning',
      message: `
Retirement Calculator Lead Submission

CONTACT INFORMATION:
Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

PERSONAL DETAILS:
Current Age: ${formData.currentAge}
Retirement Age: ${formData.retirementAge}
Years to Retirement: ${calculatedResults.yearsToRetirement}

FINANCIAL INFORMATION:
Current Savings: $${Number(formData.currentSavings).toLocaleString()}
Monthly Contribution: $${Number(formData.monthlyContribution).toLocaleString()}
Expected Annual Return: ${formData.expectedReturn}%
Inflation Rate: ${formData.inflationRate}%

RETIREMENT GOALS:
Desired Monthly Income: $${Number(formData.monthlyRetirementIncome).toLocaleString()}
Retirement Lifestyle: ${formData.retirementLifestyle}
Estimated Healthcare Costs: $${formData.healthcareCosts || '500'}/month
Additional Goals: ${formData.additionalGoals || 'None specified'}

CALCULATED RESULTS:
Total Retirement Needed: $${calculatedResults.retirementNeeded.toLocaleString()}
Projected Savings at Retirement: $${calculatedResults.projectedSavings.toLocaleString()}
Savings Gap: $${calculatedResults.savingsGap.toLocaleString()}
Recommended Monthly Contribution: $${calculatedResults.recommendedMonthly.toLocaleString()}
Inflation-Adjusted Monthly Goal: $${calculatedResults.inflationAdjustedGoal.toLocaleString()}

Submission Date: ${new Date().toLocaleString()}
      `.trim()
    };

    try {
      // Send custom admin notification email to jake@averillagencies.com
      await resendService.sendEmail({
        to: 'jake@averillagencies.com',
        subject: '💰 New Retirement Calculator Submission - Multi-Step Form Completed',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0; font-size: 28px;">Retirement Calculator Submission</h1>
              <p style="color: #ffd700; margin: 10px 0 0 0; font-size: 16px;">Multi-Step Form Completed (All ${totalSteps} Steps)</p>
            </div>
            
            <div style="padding: 30px; background-color: #f9f9f9;">
              <div style="background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%); padding: 15px; border-radius: 8px; margin-bottom: 25px;">
                <h2 style="color: #232B3B; margin: 0; font-size: 20px;">📊 High-Intent Retirement Planning Lead</h2>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">Contact Information</h3>
                <div style="display: grid; gap: 10px;">
                  <p style="margin: 0;"><strong>Name:</strong> <span style="color: #333;">${formData.fullName}</span></p>
                  <p style="margin: 0;"><strong>Email:</strong> <a href="mailto:${formData.email}" style="color: #4a90e2;">${formData.email}</a></p>
                  <p style="margin: 0;"><strong>Phone:</strong> <a href="tel:${formData.phone || 'Not provided'}" style="color: #4a90e2;">${formData.phone || 'Not provided'}</a></p>
                  <p style="margin: 0;"><strong>Submission Time:</strong> <span style="color: #333;">${new Date().toLocaleString()}</span></p>
                </div>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">Personal & Financial Profile</h3>
                <div style="background-color: #fff9f0; padding: 20px; border-radius: 6px; border-left: 4px solid #ffd700;">
                  <div style="display: grid; gap: 12px;">
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Current Age:</strong>
                      <span style="color: #232B3B; font-weight: 600;">${formData.currentAge} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Target Retirement Age:</strong>
                      <span style="color: #232B3B; font-weight: 600;">${formData.retirementAge} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Years to Retirement:</strong>
                      <span style="color: #e74c3c; font-weight: 600;">${calculatedResults.yearsToRetirement} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Current Savings:</strong>
                      <span style="color: #232B3B; font-weight: 600;">$${Number(formData.currentSavings).toLocaleString()}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Monthly Contribution:</strong>
                      <span style="color: #232B3B; font-weight: 600;">$${Number(formData.monthlyContribution).toLocaleString()}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Expected Return:</strong>
                      <span style="color: #232B3B; font-weight: 600;">${formData.expectedReturn}%</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
                      <strong style="color: #555;">Inflation Rate:</strong>
                      <span style="color: #232B3B; font-weight: 600;">${formData.inflationRate}%</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; padding: 8px 0;">
                      <strong style="color: #555;">Retirement Lifestyle:</strong>
                      <span style="color: #232B3B; font-weight: 600;">${formData.retirementLifestyle || 'Not specified'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">🎯 Retirement Goals & Calculated Results</h3>
                <div style="background: linear-gradient(135deg, #e8f4f8 0%, #f0f8ff 100%); padding: 20px; border-radius: 6px;">
                  <div style="display: grid; gap: 15px;">
                    <div style="background: white; padding: 15px; border-radius: 6px; border-left: 4px solid #4CAF50;">
                      <strong style="color: #555;">Desired Monthly Income:</strong>
                      <p style="margin: 5px 0 0 0; font-size: 20px; color: #232B3B; font-weight: bold;">
                        $${Number(formData.monthlyRetirementIncome).toLocaleString()}
                      </p>
                    </div>
                    <div style="background: white; padding: 15px; border-radius: 6px; border-left: 4px solid #2196F3;">
                      <strong style="color: #555;">Total Retirement Needed:</strong>
                      <p style="margin: 5px 0 0 0; font-size: 20px; color: #232B3B; font-weight: bold;">
                        $${calculatedResults.retirementNeeded.toLocaleString()}
                      </p>
                    </div>
                    <div style="background: white; padding: 15px; border-radius: 6px; border-left: 4px solid #FF9800;">
                      <strong style="color: #555;">Projected Savings at Retirement:</strong>
                      <p style="margin: 5px 0 0 0; font-size: 20px; color: #232B3B; font-weight: bold;">
                        $${calculatedResults.projectedSavings.toLocaleString()}
                      </p>
                    </div>
                    <div style="background: white; padding: 15px; border-radius: 6px; border-left: 4px solid ${calculatedResults.savingsGap > 0 ? '#f44336' : '#4CAF50'};">
                      <strong style="color: #555;">Savings Gap:</strong>
                      <p style="margin: 5px 0 0 0; font-size: 20px; color: ${calculatedResults.savingsGap > 0 ? '#f44336' : '#4CAF50'}; font-weight: bold;">
                        ${calculatedResults.savingsGap > 0 ? '-' : '+'}$${Math.abs(calculatedResults.savingsGap).toLocaleString()}
                      </p>
                    </div>
                    <div style="background: white; padding: 15px; border-radius: 6px; border-left: 4px solid #9C27B0;">
                      <strong style="color: #555;">Recommended Monthly Contribution:</strong>
                      <p style="margin: 5px 0 0 0; font-size: 20px; color: #232B3B; font-weight: bold;">
                        $${calculatedResults.recommendedMonthly.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style="background-color: white; padding: 25px; border-radius: 8px; margin-top: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <h3 style="color: #232B3B; margin-top: 0; border-bottom: 2px solid #ffd700; padding-bottom: 10px;">Additional Information</h3>
                <div style="padding: 15px; background-color: #f9f9f9; border-radius: 6px;">
                  <p style="margin: 0 0 10px 0;"><strong>Healthcare Cost Estimate:</strong> $${formData.healthcareCosts || '500'}/month</p>
                  <p style="margin: 0 0 10px 0;"><strong>Additional Goals:</strong> ${formData.additionalGoals || 'None specified'}</p>
                  <p style="margin: 0;"><strong>Inflation-Adjusted Monthly Goal:</strong> $${calculatedResults.inflationAdjustedGoal.toLocaleString()}</p>
                </div>
              </div>
              
              <div style="background-color: #232B3B; padding: 20px; border-radius: 8px; margin-top: 25px;">
                <h3 style="color: #ffd700; margin: 0 0 15px 0; font-size: 18px;">⚡ Priority Follow-Up Actions</h3>
                <ul style="color: white; margin: 0; padding-left: 20px; line-height: 1.8;">
                  <li>Contact within 24 hours - completed full calculator (high intent)</li>
                  <li>${calculatedResults.savingsGap > 0 ? '<strong style="color: #ffd700;">URGENT:</strong> Major savings gap - needs immediate retirement planning assistance' : 'On track but could benefit from optimization strategies'}</li>
                  <li>Discuss annuity options for guaranteed retirement income</li>
                  <li>Review healthcare cost planning - estimated $${(Number(formData.healthcareCosts || 500) * 12).toLocaleString()}/year</li>
                  <li>Lifestyle goal: ${formData.retirementLifestyle || 'Standard'} - tailor recommendations accordingly</li>
                </ul>
              </div>
              
              <div style="text-align: center; margin-top: 25px; padding: 20px; background-color: #fff9f0; border-radius: 8px;">
                <p style="color: #666; margin: 0; font-size: 14px;">
                  <strong>Page Source:</strong> /retirement-calculator - Multi-Step Form<br>
                  <span style="font-size: 12px; color: #999;">✅ High-quality lead - completed all ${totalSteps} steps of calculator</span>
                </p>
              </div>
            </div>
          </div>
        `
      });

      // Send confirmation email to user
      await resendService.sendEmail({
        to: formData.email,
        subject: 'Your Retirement Planning Analysis Results',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%); padding: 30px; text-align: center;">
              <h1 style="color: white; margin: 0;">Your Retirement Analysis Results</h1>
            </div>
            <div style="padding: 30px;">
              <h2 style="color: #232B3B;">Hello ${formData.fullName},</h2>
              <p style="color: #666; font-size: 16px; line-height: 1.6;">
                Thank you for completing our retirement planning calculator. Based on your inputs, here are your personalized results:
              </p>
              
              <div style="background-color: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #232B3B;">Your Retirement Projections:</h3>
                <ul style="color: #555; line-height: 1.8;">
                  <li><strong>Years to Retirement:</strong> ${calculatedResults.yearsToRetirement}</li>
                  <li><strong>Total Needed:</strong> $${calculatedResults.retirementNeeded.toLocaleString()}</li>
                  <li><strong>Projected Savings:</strong> $${calculatedResults.projectedSavings.toLocaleString()}</li>
                  <li><strong>Savings Gap:</strong> $${Math.abs(calculatedResults.savingsGap).toLocaleString()} ${calculatedResults.savingsGap > 0 ? '(shortfall)' : '(surplus)'}</li>
                  <li><strong>Recommended Monthly Savings:</strong> $${calculatedResults.recommendedMonthly.toLocaleString()}</li>
                </ul>
              </div>
              
              <h3 style="color: #232B3B;">Next Steps:</h3>
              <p style="color: #666; line-height: 1.6;">
                Our retirement specialists will review your analysis and reach out within 24 hours to discuss strategies to help you achieve your retirement goals.
              </p>
              
              <p style="color: #666; line-height: 1.6; margin-top: 20px;">
                <strong>Want to discuss your results immediately?</strong><br>
                Call us at 800-922-1894 to speak with a retirement planning specialist.
              </p>
              
              <div style="text-align: center; margin-top: 30px;">
                <a href="https://annuityanswers.com" style="background-color: #ffd700; color: #232B3B; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                  Visit Our Website
                </a>
              </div>
            </div>
          </div>
        `
      });
      
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error sending email:', error);
      // Still mark as success even if email fails
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <HeroTitle>Retirement Planning Calculator</HeroTitle>
          <HeroSubtitle>
            Take control of your retirement future with our comprehensive planning tool. 
            Get personalized insights and expert guidance in just 3 simple steps.
          </HeroSubtitle>
        </Container>
      </HeroSection>
      
      <MainContent>
        <Container>

          <CalculatorCard>
            <ProgressBar>
              <ProgressFill progress={progress} />
            </ProgressBar>

            <StepIndicator>
              Step {currentStep} of {totalSteps}
            </StepIndicator>

            {!results ? (
              <Form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                {/* Step 1: Personal Information */}
                <FormSection isActive={currentStep === 1}>
                  <h2>Personal Information</h2>
                  
                  <FormGroup>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                    />
                    {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                    />
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="currentAge">Current Age *</Label>
                    <Input
                      type="number"
                      id="currentAge"
                      name="currentAge"
                      value={formData.currentAge}
                      onChange={handleInputChange}
                      placeholder="45"
                      min="18"
                      max="100"
                    />
                    {errors.currentAge && <ErrorMessage>{errors.currentAge}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="retirementAge">Desired Retirement Age *</Label>
                    <Input
                      type="number"
                      id="retirementAge"
                      name="retirementAge"
                      value={formData.retirementAge}
                      onChange={handleInputChange}
                      placeholder="65"
                      min="50"
                      max="100"
                    />
                    {errors.retirementAge && <ErrorMessage>{errors.retirementAge}</ErrorMessage>}
                  </FormGroup>
                </FormSection>

                {/* Step 2: Financial Information */}
                <FormSection isActive={currentStep === 2}>
                  <h2>Financial Information</h2>
                  
                  <FormGroup>
                    <Label htmlFor="currentSavings">Current Retirement Savings *</Label>
                    <Input
                      type="number"
                      id="currentSavings"
                      name="currentSavings"
                      value={formData.currentSavings}
                      onChange={handleInputChange}
                      placeholder="100000"
                      min="0"
                    />
                    {errors.currentSavings && <ErrorMessage>{errors.currentSavings}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="monthlyContribution">Monthly Contribution *</Label>
                    <Input
                      type="number"
                      id="monthlyContribution"
                      name="monthlyContribution"
                      value={formData.monthlyContribution}
                      onChange={handleInputChange}
                      placeholder="500"
                      min="0"
                    />
                    {errors.monthlyContribution && <ErrorMessage>{errors.monthlyContribution}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="expectedReturn">Expected Annual Return (%)</Label>
                    <Select
                      id="expectedReturn"
                      name="expectedReturn"
                      value={formData.expectedReturn}
                      onChange={handleInputChange}
                    >
                      <option value="5">5% (Conservative)</option>
                      <option value="7">7% (Moderate)</option>
                      <option value="9">9% (Aggressive)</option>
                      <option value="11">11% (Very Aggressive)</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="inflationRate">Expected Inflation Rate (%)</Label>
                    <Select
                      id="inflationRate"
                      name="inflationRate"
                      value={formData.inflationRate}
                      onChange={handleInputChange}
                    >
                      <option value="2">2% (Low)</option>
                      <option value="3">3% (Average)</option>
                      <option value="4">4% (High)</option>
                      <option value="5">5% (Very High)</option>
                    </Select>
                  </FormGroup>
                </FormSection>

                {/* Step 3: Retirement Goals */}
                <FormSection isActive={currentStep === 3}>
                  <h2>Retirement Goals</h2>
                  
                  <FormGroup>
                    <Label htmlFor="monthlyRetirementIncome">Desired Monthly Income in Retirement *</Label>
                    <Input
                      type="number"
                      id="monthlyRetirementIncome"
                      name="monthlyRetirementIncome"
                      value={formData.monthlyRetirementIncome}
                      onChange={handleInputChange}
                      placeholder="5000"
                      min="0"
                    />
                    {errors.monthlyRetirementIncome && <ErrorMessage>{errors.monthlyRetirementIncome}</ErrorMessage>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="retirementLifestyle">Retirement Lifestyle</Label>
                    <Select
                      id="retirementLifestyle"
                      name="retirementLifestyle"
                      value={formData.retirementLifestyle}
                      onChange={handleInputChange}
                    >
                      <option value="basic">Basic - Essential expenses only</option>
                      <option value="moderate">Moderate - Comfortable with some extras</option>
                      <option value="active">Active - Travel and hobbies</option>
                      <option value="luxury">Luxury - Premium lifestyle</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="healthcareCosts">Estimated Monthly Healthcare Costs</Label>
                    <Input
                      type="number"
                      id="healthcareCosts"
                      name="healthcareCosts"
                      value={formData.healthcareCosts}
                      onChange={handleInputChange}
                      placeholder="500"
                      min="0"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="additionalGoals">Additional Goals or Concerns</Label>
                    <TextArea
                      id="additionalGoals"
                      name="additionalGoals"
                      value={formData.additionalGoals}
                      onChange={handleInputChange}
                      placeholder="Tell us about any specific retirement goals, concerns, or questions you have..."
                    />
                  </FormGroup>
                </FormSection>

                <ButtonGroup>
                  {currentStep > 1 && (
                    <Button type="button" variant="secondary" onClick={handlePrevious}>
                      Previous
                    </Button>
                  )}
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={isSubmitting}
                    style={{ marginLeft: currentStep === 1 ? 'auto' : '0' }}
                  >
                    {currentStep === totalSteps ? (isSubmitting ? 'Calculating...' : 'Calculate Results') : 'Next'}
                  </Button>
                </ButtonGroup>
              </Form>
            ) : (
              <div>
                <ResultsSection>
                  <ResultsTitle>Your Personalized Retirement Plan</ResultsTitle>
                  
                  <ResultsGrid>
                    <ResultCard>
                      <ResultLabel>Years to Retirement</ResultLabel>
                      <ResultValue>{results.yearsToRetirement} years</ResultValue>
                    </ResultCard>

                    <ResultCard>
                      <ResultLabel>Total Retirement Needed</ResultLabel>
                      <ResultValue>{formatCurrency(results.retirementNeeded)}</ResultValue>
                    </ResultCard>

                    <ResultCard>
                      <ResultLabel>Projected Savings</ResultLabel>
                      <ResultValue>{formatCurrency(results.projectedSavings)}</ResultValue>
                    </ResultCard>

                    <ResultCard>
                      <ResultLabel>Savings Gap</ResultLabel>
                      <ResultValue style={{ color: results.savingsGap > 0 ? '#ef4444' : '#10b981' }}>
                        {formatCurrency(results.savingsGap)}
                      </ResultValue>
                    </ResultCard>

                    <ResultCard>
                      <ResultLabel>Current Monthly Contribution</ResultLabel>
                      <ResultValue>{formatCurrency(Number(formData.monthlyContribution))}</ResultValue>
                    </ResultCard>

                    <ResultCard>
                      <ResultLabel>Recommended Monthly Contribution</ResultLabel>
                      <ResultValue>{formatCurrency(results.recommendedMonthly)}</ResultValue>
                    </ResultCard>
                  </ResultsGrid>

                  <div style={{ background: 'white', padding: theme.spacing.lg, borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: theme.spacing.md }}>Your Retirement Analysis Summary</h3>
                    <p style={{ lineHeight: '1.6', color: theme.colors.text.secondary }}>
                      Based on your inputs, you'll need approximately <strong>{formatCurrency(results.retirementNeeded)}</strong> to 
                      maintain your desired lifestyle in retirement. With your current savings and contribution rate, 
                      you're projected to have <strong>{formatCurrency(results.projectedSavings)}</strong> by retirement.
                    </p>
                    {results.savingsGap > 0 && (
                      <p style={{ lineHeight: '1.6', color: theme.colors.text.secondary, marginTop: theme.spacing.md }}>
                        To close the gap of <strong>{formatCurrency(results.savingsGap)}</strong>, we recommend increasing 
                        your monthly contribution to <strong>{formatCurrency(results.recommendedMonthly)}</strong>. 
                        An Annuity Specialist will contact you shortly to discuss personalized strategies to help you achieve your retirement goals.
                      </p>
                    )}
                  </div>
                </ResultsSection>

                {submitSuccess && (
                  <SuccessMessage>
                    ✓ Your results have been sent! A retirement specialist will contact you within 24 hours.
                  </SuccessMessage>
                )}

                <ButtonGroup style={{ marginTop: theme.spacing.xl }}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setCurrentStep(1);
                      setResults(null);
                      setSubmitSuccess(false);
                    }}
                  >
                    Start New Calculation
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => window.print()}
                  >
                    Print Results
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </CalculatorCard>
        </Container>
      </MainContent>
      
      <CTASection>
        <Container>
          <CTATitle>Ready to Secure Your Retirement Future?</CTATitle>
          <CTADescription>
            Get personalized guidance from our retirement specialists. Schedule a free consultation 
            to discuss your retirement goals and discover strategies tailored to your situation.
          </CTADescription>
          <CTAButton onClick={openCalendly}>
            Schedule Free Consultation
          </CTAButton>
          <CTAPhone href="tel:800-922-1894">
            Or Call: 800-922-1894
          </CTAPhone>
        </Container>
      </CTASection>
    </SharedLayout>
  );
};